// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import Search from "@store/Library/Components/Toolkit/Icons/Search";
import { filtersProps } from "@store/Themes/common/props/filters";
import getColor from "@insite/mobius/utilities/getColor";
import updateCmsLibraryParameters from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/UpdateCmsLibraryParameters";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { getCmsLibrarySessionKey } from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/LoadCmsLibrary";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    currentLibrary: state.store.data.cmsLibrary.currentLibrary,
    parameters: state.store.data.cmsLibrary.parameters,
});

const mapDispatchToProps = {
    updateCmsLibraryParameters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface CmsLibrarySearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
}

export const searchWithinStyles: CmsLibrarySearchWithinStyles = {
    wrapper: {
        css: css`
            margin-bottom: ${pxToRem(15)};
        `,
    },
    titleText: {
        ...filtersProps.title,
        forwardAs: "label",
    },
    searchTextField: {
        backgroundColor: "common.accent",
        iconProps: {
            src: Search,
            css: css`
                color: ${getColor("primary")};
            `,
        },
        mergeCss: true,
        css: css`
            border: none !important;
        `,
    },
};

const styles = searchWithinStyles;

const CmsLibrarySearchWithin: FC<Props> = ({ updateCmsLibraryParameters, currentLibrary, parameters }) => {
    const sessionKey = getCmsLibrarySessionKey(currentLibrary);

    const [searchText, setSearchText] = useState(() => sessionStorage.getItem(sessionKey) ?? "");

    useEffect(() => {
        setSearchText(sessionStorage.getItem(sessionKey) ?? "");
    }, [currentLibrary]);

    const doSearch = (clear?: boolean) => {
        const searchTerm = clear ? "" : searchText;

        if (searchTerm) {
            sessionStorage.setItem(sessionKey, searchTerm);
        } else {
            sessionStorage.removeItem(sessionKey);
        }

        updateCmsLibraryParameters({ libraryType: currentLibrary, search: searchTerm, page: 1 });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    const searchFieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setSearchText(value);

        if (value.length === 0) {
            doSearch(true);
        }
    };

    const label = translate(`Search Within Category`);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography forwardAs="label" htmlFor="librariesKeywordSearch" {...styles.titleText}>
                {label}
            </Typography>
            <TextField
                {...styles.searchTextField}
                iconClickableProps={{
                    ...styles.searchTextField?.iconClickableProps,
                }}
                clickableText={label}
                value={searchText}
                onChange={searchFieldChangeHandler}
                onKeyDown={handleKeyDown}
                placeholder={translate("Enter Keywords")}
                type="search"
                data-test-selector="cmsLibrarySearchWithin"
                id="librariesKeywordSearch"
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CmsLibrarySearchWithin),
    definition: {
        group: "Common",
        displayName: "CMS Library Search Within",
    },
};

export default widgetModule;
